import { USERS_URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: USERS_URL + "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),

    getRoles: builder.query({
      query: () => `${USERS_URL}/roles`,
      providesTags: ["Roles"],
    }),

    createRole: builder.mutation({
      query: (roleData) => ({
        url: `${USERS_URL}/roles`,
        method: "POST",
        body: roleData,
      }),
      invalidatesTags: ["Roles"],
    }),

    updateRole: builder.mutation({
      query: ({ id, ...roleData }) => ({
        url: `${USERS_URL}/roles/${id}`,
        method: "PUT",
        body: roleData,
      }),
      invalidatesTags: ["Roles"],
    }),

    deleteRole: builder.mutation({
      query: (id) => ({
        url: `${USERS_URL}/roles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Roles"],
    }),

    forgotPassword: builder.mutation({
      query: (email) => ({
        url: USERS_URL + "/users/forgot-password",
        method: "POST",
        body: { email },
      }),
    }),

    verifyOtp: builder.mutation({
      query: ({ email, otp }) => ({
        url: USERS_URL + "/users/verify-otp",
        method: "POST",
        body: { email, otp },
      }),
    }),

    resendOtp: builder.mutation({
      query: (email) => ({
        url: USERS_URL + "/users/resend-otp",
        method: "POST",
        body: { email },
      }),
    }),

    resetPassword: builder.mutation({
      query: ({ email, otp, newPassword }) => ({
        url: USERS_URL + "/users/reset-password",
        method: "POST",
        body: { email, otp, newPassword },
      }),
    }),

    postAcademy: builder.mutation({
      query: (formData) => ({
        url: USERS_URL + "/academies",
        method: "POST",
        body: formData,
      }),
    }),

    getAcademy: builder.query({
      query: () => ({
        url: USERS_URL + "/academies",
        method: "GET",
      }),
      providesTags: ["Academy"],
    }),

    // Get department - shared across all academies
    getDepartment: builder.query({
      query: () => ({
        url: USERS_URL + "/departments",
        method: "GET",
      }),
      providesTags: ["Departments"],
    }),

    // Delete Department
    deleteDepartment: builder.mutation({
      query: (id) => ({
        url: `${USERS_URL}/departments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Departments"],
    }),

    // Post Department
    createDepartment: builder.mutation({
      query: (departmentData) => ({
        url: USERS_URL + "/departments",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(departmentData),
      }),
      invalidatesTags: ["Departments"],
    }),

    // updateAcademy: builder.mutation({
    //   query: ({ academyId, name, location, description, image }) => {
    //     const formData = new FormData();
    //     formData.append("name", name);
    //     formData.append("location", location);
    //     formData.append("description", description);
    //     if (image) {
    //       formData.append("image", image);
    //     }

    //     return {
    //       url: `${USERS_URL}/academies/${academyId}`,
    //       method: "PUT",
    //       body: formData,
    //     };
    //   },
    // }),

    updateAcademy: builder.mutation({
      query: ({ academyId, name, location, description, image }) => {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("location", location);
        formData.append("description", description);

        // Only append image if it's a File object (and not null or a string)
        if (image instanceof File) {
          formData.append("image", image);
        }

        return {
          url: `${USERS_URL}/academies/${academyId}`,
          method: "PUT",
          body: formData,
        };
      },
    }),

    // Delete Academy
    deleteAcademy: builder.mutation({
      query: (id) => ({
        url: USERS_URL + `/academies/${id}`,
        method: "DELETE",
      }),
    }),

    getUsers: builder.query({
      query: () => ({
        url: USERS_URL + "/users",
        method: "GET",
      }),
      providesTags: ["users"],
    }),

    // Get all users - requires only authentication, no specific role needed
    getAllUsers: builder.query({
      query: () => ({
        url: USERS_URL + "/users/all",
        method: "GET",
      }),
      providesTags: ["users"],
    }),

    createUser: builder.mutation({
      query: (userData) => {
        const formData = new FormData();
        formData.append("email", userData.email);
        formData.append("password", userData.password);
        formData.append("name", userData.name);
        formData.append("employeeId", userData.employeeId);

        // Only append academyId if it exists
        if (userData.academyId) {
          formData.append("academyId", userData.academyId);
        }

        // Only append departmentId if it exists
        if (userData.departmentId) {
          formData.append("departmentId", userData.departmentId);
        }

        if (userData.divisionId) {
          formData.append("divisionId", userData.divisionId);
        }

        formData.append("roleId", userData.roleId);

        if (userData.image) {
          formData.append("image", userData.image);
        }

        return {
          url: USERS_URL + "/users",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["users"],
    }),

    updateUser: builder.mutation({
      query: ({ id, ...userData }) => ({
        url: `${USERS_URL}/users/${id}`,
        method: "PUT",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),

    getUserByEmail: builder.query({
      query: (email) => ({
        url: `${USERS_URL}/users/email`,
        method: "GET",
        params: { email },
      }),
    }),

    updateUserImage: builder.mutation({
      query: ({ email, image }) => {
        const formData = new FormData();
        formData.append("email", email);
        if (image) {
          formData.append("image", image);
        }
        return {
          url: USERS_URL + "/users/image",
          method: "PUT",
          body: formData,
        };
      },
    }),

    changePassword: builder.mutation({
      query: ({ email, oldPassword, newPassword }) => ({
        url: USERS_URL + "/users/change-password",
        method: "PUT",
        body: { email, oldPassword, newPassword },
      }),
    }),

    logout: builder.mutation({
      query: () => ({
        url: USERS_URL + "/auth/logout",
        method: "POST",
      }),
    }),

    getAllAcademies: builder.query({
      query: () => ({
        url: USERS_URL + "/academies",
        method: "GET",
      }),
    }),

    getAllDepartments: builder.query({
      query: (academyId) => ({
        url: USERS_URL + "/departments",
        method: "GET",
        params: { academyId },
      }),
    }),

    getAcademyById: builder.query({
      query: (id) => ({
        url: `${USERS_URL}/academies/${id}`,
        method: "GET",
      }),
    }),

    getDepartmentById: builder.query({
      query: (id) => ({
        url: `${USERS_URL}/departments/${id}`,
        method: "GET",
      }),
    }),

    softDeleteUser: builder.mutation({
      query: (id) => ({
        url: `${USERS_URL}/users/${id}`,
        method: "DELETE",
      }),
    }),

    updateDepartment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${USERS_URL}/departments/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Department"],
    }),

    // Division Management - shared across all academies
    getDivisions: builder.query({
      query: () => ({
        url: USERS_URL + "/divisions",
        method: "GET",
      }),
      providesTags: ["Divisions"],
    }),

    createDivision: builder.mutation({
      query: (divisionData) => ({
        url: USERS_URL + "/divisions",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(divisionData),
      }),
      invalidatesTags: ["Divisions"],
    }),

    updateDivision: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${USERS_URL}/divisions/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Divisions"],
    }),

    deleteDivision: builder.mutation({
      query: (id) => ({
        url: `${USERS_URL}/divisions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Divisions"],
    }),

    getDivisionById: builder.query({
      query: (id) => ({
        url: `${USERS_URL}/divisions/${id}`,
        method: "GET",
      }),
    }),
    adminResetPassword: builder.mutation({
      query: ({ id, newPassword }) => ({
        url: `${USERS_URL}/users/${id}/reset-password-admin`,
        method: "PUT",
        body: { newPassword },
      }),
    }),
  }),
});

export const {
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useResetPasswordMutation,
  usePostAcademyMutation,
  useGetAcademyQuery,
  useDeleteAcademyMutation,
  useUpdateAcademyMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetDepartmentQuery,
  useCreateDepartmentMutation,
  useGetUsersQuery,
  useGetAllUsersQuery,
  useGetUserByEmailQuery,
  useUpdateUserImageMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useGetAcademyByIdQuery,
  useGetDepartmentByIdQuery,
  useSoftDeleteUserMutation,
  useDeleteDepartmentMutation,
  useUpdateDepartmentMutation,
  useGetDivisionsQuery,
  useCreateDivisionMutation,
  useUpdateDivisionMutation,
  useDeleteDivisionMutation,
  useGetDivisionByIdQuery,
  useAdminResetPasswordMutation,
} = usersApiSlice;
